import type { EconomicContributionScreeningSnapshot, EconomicEvidenceBasis } from '../../types/economicContribution';
import {
  ECONOMIC_RESEARCH_SCHEMA_VERSION,
  type EconomicResearchHeldSummary,
  type EconomicResearchResultSnapshot,
} from '../../types/economicResearch';
import { hashEconomicModelInput } from './modelUtils';

type PublishedBasis = Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>;

export function buildEconomicResearchResultSnapshot(input: {
  siteFingerprint: string;
  screening: EconomicContributionScreeningSnapshot;
  held?: readonly EconomicResearchHeldSummary[];
  excluded?: readonly EconomicResearchHeldSummary[];
  generatedAt?: string;
}): EconomicResearchResultSnapshot {
  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const policyValidRecords = input.screening.comparableMatches.map((match) => {
    const record = match.record;
    const jobsObservation = record.permanentJobsRange ?? record.permanentJobs;
    const jobsValue = jobsObservation?.value;
    const permanentJobs = jobsObservation && jobsValue != null
      ? {
          low: typeof jobsValue === 'number' ? jobsValue : jobsValue.low,
          high: typeof jobsValue === 'number' ? jobsValue : jobsValue.high,
          evidenceBasis: jobsObservation.evidenceBasis,
          phaseBasis: jobsObservation.phaseBasis,
          sources: jobsObservation.sources,
        }
      : undefined;
    const mw = record.mw ? {
      value: record.mw.value.value,
      basis: record.mw.value.basis,
      phaseBasis: record.mw.phaseBasis,
      sources: record.mw.sources,
    } : undefined;
    const factSources = [...(mw?.sources ?? []), ...(permanentJobs?.sources ?? [])]
      .filter((source, index, all) => all.findIndex((candidate) => (
        candidate.id === source.id
        && candidate.url === source.url
        && candidate.excerpt === source.excerpt
        && candidate.locator === source.locator
      )) === index);
    const extractedClaims = [
      ...(mw ? [{
        kind: 'extracted-claim' as const,
        label: 'Documented capacity',
        detail: `${mw.value} MW ${mw.basis} · ${mw.phaseBasis}`,
      }] : []),
      ...(permanentJobs ? [{
        kind: 'extracted-claim' as const,
        label: 'Permanent onsite employment',
        detail: `${permanentJobs.low === permanentJobs.high ? permanentJobs.low : `${permanentJobs.low}–${permanentJobs.high}`} jobs · ${permanentJobs.evidenceBasis} · ${permanentJobs.phaseBasis}`,
      }] : []),
    ];
    return {
      projectId: record.projectId,
      facilityName: record.facilityName,
      locality: record.location.locality,
      stateCode: record.location.stateCode,
      score: match.score,
      ...(mw ? { mw } : {}),
      ...(permanentJobs ? { permanentJobs } : {}),
      reasons: match.reasons,
      incompatibilities: match.incompatibilities,
      compatibleForJobsPerMw: match.compatibleForJobsPerMw,
      ...(record.waterTimeline?.length ? { waterTimeline: record.waterTimeline } : {}),
      ...(record.coolingObservations?.length ? { coolingObservations: record.coolingObservations } : {}),
      ...(record.powerObservations?.length ? { powerObservations: record.powerObservations } : {}),
      ...(record.deliveryEvents?.length ? { deliveryEvents: record.deliveryEvents } : {}),
      evidenceTrail: [
        ...factSources.map((source) => ({
          kind: 'official-record' as const,
          label: source.publisher,
          detail: source.title,
          sourceUrl: source.url,
          excerpt: source.excerpt,
          locator: source.locator,
        })),
        ...extractedClaims,
        {
          kind: 'compatibility-decision' as const,
          label: match.compatibleForJobsPerMw ? 'Compatible comparison' : 'Individual evidence only',
          detail: match.compatibleForJobsPerMw
            ? match.reasons.join(' · ') || 'Entity, phase and MW basis are compatible.'
            : match.incompatibilities.join(' · ') || 'This record is not used in a jobs-per-MW aggregate.',
        },
      ],
    };
  });
  const basisCounts: Partial<Record<PublishedBasis, number>> = {};
  for (const comparable of policyValidRecords) {
    const recordBases = new Set<PublishedBasis>([
      ...(comparable.permanentJobs ? [comparable.permanentJobs.evidenceBasis] : []),
      ...(comparable.waterTimeline ?? []).map((observation) => observation.evidenceBasis),
      ...(comparable.coolingObservations ?? []).map((observation) => observation.evidenceBasis),
      ...(comparable.powerObservations ?? []).map((observation) => observation.evidenceBasis),
      ...(comparable.deliveryEvents ?? []).map((observation) => observation.evidenceBasis),
    ]);
    for (const basis of recordBases) basisCounts[basis] = (basisCounts[basis] ?? 0) + 1;
  }
  const held = [...(input.held ?? [])];
  const excluded = [...(input.excluded ?? [])];
  return {
    schemaVersion: ECONOMIC_RESEARCH_SCHEMA_VERSION,
    snapshotId: `economic-research-${hashEconomicModelInput({
      siteFingerprint: input.siteFingerprint,
      screeningSnapshotId: input.screening.snapshotId,
      records: policyValidRecords.map((record) => record.projectId),
      held: held.map((lead) => lead.leadId),
      excluded: excluded.map((lead) => lead.leadId),
    }).slice(0, 24)}`,
    siteFingerprint: input.siteFingerprint,
    generatedAt,
    policyValidRecords,
    held,
    excluded,
    basisCounts,
    fiscalBaselineDrafts: [],
  };
}
